/**
 * OrderForm - Form for placing buy/sell orders.
 * Supports both market and limit order types.
 */

import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { cn } from '../../lib/utils'
import type { NewOrder, OrderSide, OrderType } from '../../engine/types'

export interface OrderFormProps {
  /** User's available balance */
  userBalance: number
  /** Whether limit orders are enabled */
  enableLimitOrders?: boolean
  /** Whether market orders are enabled */
  enableMarketOrders?: boolean
  /** Callback when order is submitted */
  onPlaceOrder: (order: NewOrder) => void
  /** Price selected from elsewhere in the UI (for example, the order book) */
  selectedPrice?: number
  /** Sellable Yes shares available right now */
  availableYesShares?: number
  /** Optional className */
  className?: string
}

/** Form validation errors */
interface FormErrors {
  price?: string
  quantity?: string
  general?: string
}

/**
 * OrderForm allows users to place buy/sell orders.
 */
export const OrderForm = memo(function OrderForm({
  userBalance,
  enableLimitOrders = true,
  enableMarketOrders = true,
  onPlaceOrder,
  selectedPrice,
  availableYesShares = 0,
  className,
}: OrderFormProps) {
  // Form state
  const [side, setSide] = useState<OrderSide>('buy')
  const [orderType, setOrderType] = useState<OrderType>(
    enableLimitOrders ? 'limit' : 'market'
  )
  const [priceInput, setPriceInput] = useState(
    selectedPrice ? String(Math.round(selectedPrice * 100)) : ''
  )
  const [quantityInput, setQuantityInput] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})

  useEffect(() => {
    if (selectedPrice === undefined) {
      return
    }

    setPriceInput(String(Math.round(selectedPrice * 100)))
    setErrors((prev) => ({ ...prev, price: undefined, general: undefined }))
  }, [selectedPrice])

  // Parse inputs to numbers
  const price = useMemo(() => {
    const cents = parseInt(priceInput, 10)
    return isNaN(cents) ? null : cents / 100
  }, [priceInput])

  const quantity = useMemo(() => {
    const qty = parseInt(quantityInput, 10)
    return isNaN(qty) ? null : qty
  }, [quantityInput])

  // Calculate estimated cost
  const estimatedCost = useMemo(() => {
    if (quantity === null || quantity <= 0) return null
    if (orderType === 'limit' && price !== null) {
      return quantity * price
    }
    // For market buys, show worst-case cost. Market sells depend on current liquidity.
    if (orderType === 'market' && side === 'buy') {
      return quantity * 1.0 // Worst case: $1 per share
    }
    return null
  }, [orderType, price, quantity, side])

  // Validate form
  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {}

    // Validate price for limit orders
    if (orderType === 'limit') {
      if (price === null) {
        newErrors.price = 'Enter a price'
      } else if (price < 0.01 || price > 0.99) {
        newErrors.price = 'Price must be 1¢ to 99¢'
      }
    }

    // Validate quantity
    if (quantity === null) {
      newErrors.quantity = 'Enter quantity'
    } else if (quantity <= 0 || !Number.isInteger(quantity)) {
      newErrors.quantity = 'Must be a positive whole number'
    }

    // Validate buy-side balance
    if (side === 'buy' && estimatedCost !== null && estimatedCost > userBalance) {
      newErrors.general = `Insufficient balance. Need $${estimatedCost.toFixed(2)}, have $${userBalance.toFixed(2)}`
    }

    if (side === 'sell' && quantity !== null && quantity > availableYesShares) {
      newErrors.general = `You can sell up to ${availableYesShares} shares right now.`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [availableYesShares, estimatedCost, orderType, price, quantity, side, userBalance])

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()

      if (!validate()) return

      const order: NewOrder = {
        side,
        type: orderType,
        price: orderType === 'limit' ? price! : 0,
        quantity: quantity!,
      }

      onPlaceOrder(order)

      // Reset form
      setQuantityInput('')
      setErrors({})
    },
    [validate, side, orderType, price, quantity, onPlaceOrder]
  )

  // Handle price input
  const handlePriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '')
      // Limit to 2 digits (1-99)
      if (value.length <= 2) {
        setPriceInput(value)
      }
      setErrors((prev) => ({ ...prev, price: undefined }))
    },
    []
  )

  // Handle quantity input
  const handleQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '')
      setQuantityInput(value)
      setErrors((prev) => ({ ...prev, quantity: undefined, general: undefined }))
    },
    []
  )

  const isSubmitDisabled =
    (orderType === 'limit' && !priceInput) ||
    !quantityInput ||
    (side === 'buy' && estimatedCost !== null && estimatedCost > userBalance) ||
    (side === 'sell' && quantity !== null && quantity > availableYesShares)

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('rounded-lg border border-border bg-card p-4', className)}
    >
      <h3 className="mb-4 text-sm font-medium text-foreground">Place Order</h3>

      {/* Buy/Sell Toggle */}
      <div className="mb-4">
        <label className="mb-1 block text-xs text-muted-foreground">
          Action
        </label>
        <div className="flex rounded-md border border-border">
          <button
            type="button"
            onClick={() => setSide('buy')}
            className={cn(
              'flex-1 rounded-l-md px-4 py-2 text-sm font-medium transition-colors',
              side === 'buy'
                ? 'bg-green-500 text-white'
                : 'bg-background text-muted-foreground hover:bg-muted'
            )}
            aria-pressed={side === 'buy'}
          >
            Buy
          </button>
          <button
            type="button"
            onClick={() => setSide('sell')}
            className={cn(
              'flex-1 rounded-r-md px-4 py-2 text-sm font-medium transition-colors',
              side === 'sell'
                ? 'bg-red-500 text-white'
                : 'bg-background text-muted-foreground hover:bg-muted'
            )}
            aria-pressed={side === 'sell'}
          >
            Sell
          </button>
        </div>
      </div>

      {/* Order Type Toggle (if both enabled) */}
      {enableLimitOrders && enableMarketOrders && (
        <div className="mb-4">
          <label className="mb-1 block text-xs text-muted-foreground">
            Order Type
          </label>
          <div className="flex rounded-md border border-border">
            <button
              type="button"
              onClick={() => setOrderType('limit')}
              className={cn(
                'flex-1 rounded-l-md px-4 py-2 text-sm font-medium transition-colors',
                orderType === 'limit'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              )}
              aria-pressed={orderType === 'limit'}
            >
              Limit
            </button>
            <button
              type="button"
              onClick={() => setOrderType('market')}
              className={cn(
                'flex-1 rounded-r-md px-4 py-2 text-sm font-medium transition-colors',
                orderType === 'market'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              )}
              aria-pressed={orderType === 'market'}
            >
              Market
            </button>
          </div>
        </div>
      )}

      {/* Price Input (limit orders only) */}
      {orderType === 'limit' && (
        <div className="mb-4">
          <label
            htmlFor="price-input"
            className="mb-1 block text-xs text-muted-foreground"
          >
            Price (1¢ - 99¢)
          </label>
          <div className="relative">
            <input
              id="price-input"
              type="text"
              inputMode="numeric"
              value={priceInput}
              onChange={handlePriceChange}
              placeholder="50"
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 pr-8 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary',
                errors.price ? 'border-red-500' : 'border-border'
              )}
              aria-invalid={!!errors.price}
              aria-describedby={errors.price ? 'price-error' : undefined}
              autoComplete="off"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              ¢
            </span>
          </div>
          {errors.price && (
            <p id="price-error" className="mt-1 text-xs text-red-500">
              {errors.price}
            </p>
          )}
          {price !== null && (
            <p className="mt-1 text-xs text-muted-foreground">
              = {Math.round(price * 100)}% probability
            </p>
          )}
        </div>
      )}

      {/* Market order info */}
      {orderType === 'market' && (
        <div className="mb-4 rounded-md bg-muted/50 p-3">
          <p className="text-xs text-muted-foreground">
            <strong>Market Order:</strong> Executes immediately at the best
            available price. May cost more than expected if there&apos;s low
            liquidity.
          </p>
        </div>
      )}

      {/* Quantity Input */}
      <div className="mb-4">
        <label
          htmlFor="quantity-input"
          className="mb-1 block text-xs text-muted-foreground"
        >
          Number of Shares
        </label>
        <input
          id="quantity-input"
          type="text"
          inputMode="numeric"
          value={quantityInput}
          onChange={handleQuantityChange}
          placeholder="10"
          className={cn(
            'w-full rounded-md border bg-background px-3 py-2 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-primary',
            errors.quantity ? 'border-red-500' : 'border-border'
          )}
          aria-invalid={!!errors.quantity}
          aria-describedby={errors.quantity ? 'quantity-error' : undefined}
          autoComplete="off"
        />
        {errors.quantity && (
          <p id="quantity-error" className="mt-1 text-xs text-red-500">
            {errors.quantity}
          </p>
        )}
        {side === 'sell' && (
          <p className="mt-1 text-xs text-muted-foreground">
            Available to sell: {availableYesShares} shares
          </p>
        )}
      </div>

      {/* Cost Preview */}
      {estimatedCost !== null && (
        <div className="mb-4 rounded-md bg-muted/50 p-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {side === 'sell'
                ? orderType === 'market'
                  ? 'Estimated Proceeds:'
                  : 'Total Proceeds:'
                : orderType === 'market'
                  ? 'Max Cost:'
                  : 'Total Cost:'}
            </span>
            <span
              className={cn(
                'font-medium tabular-nums',
                side === 'buy' && estimatedCost > userBalance ? 'text-red-500' : 'text-foreground'
              )}
            >
              ${estimatedCost.toFixed(2)}
            </span>
          </div>
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-muted-foreground">
              {side === 'buy' ? 'Your Balance:' : 'Sellable Shares:'}
            </span>
            <span className="font-medium tabular-nums text-foreground">
              {side === 'buy' ? `$${userBalance.toFixed(2)}` : availableYesShares}
            </span>
          </div>
        </div>
      )}

      {/* General error */}
      {errors.general && (
        <div className="mb-4 rounded-md bg-red-500/10 p-3">
          <p className="text-xs text-red-500">{errors.general}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitDisabled}
        className={cn(
          'w-full rounded-md px-4 py-2 text-sm font-medium transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          side === 'buy'
            ? 'bg-green-500 text-white hover:bg-green-600 disabled:bg-green-500/50'
            : 'bg-red-500 text-white hover:bg-red-600 disabled:bg-red-500/50',
          'disabled:cursor-not-allowed'
        )}
      >
        {side === 'buy' ? 'Buy' : 'Sell'}{' '}
        {quantity !== null && quantity > 0 ? `${quantity} shares` : 'Shares'}
      </button>

      {/* Beginner hint */}
      <p className="mt-3 text-xs text-muted-foreground">
        {side === 'buy'
          ? 'Buying shares means you think this will happen.'
          : 'Selling shares closes part of the Yes position you already own.'}
      </p>
    </form>
  )
})
